import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils.js';
import { disabledFade, focusRing } from '../lib/focus.js';

/* ===========================================================================
 * Admin-console button, ported from the sister app's anatomy (its
 * DESIGN_SYSTEM.md) onto Hadir's own tokens.
 *
 *   size   padding      type              min height
 *   sm     8 / 12       12.5px · 600      32px
 *   md     12 / 16      13.5px · 600      40px   (default)
 *   lg     16 / 32      14.5px · 600      48px
 *
 * Geometry is padding + a min-height floor rather than a fixed height, so a
 * button that wraps grows instead of clipping its own label — which matters in
 * Arabic and German, where labels run long.
 *
 * TYPE. Every size sits in the handoff's label band (12.5–13.5px · 600,
 * docs/design-rules.md §1). `sm` used to carry `text-badge` (11px) and was the
 * last live consumer of that step anywhere in the app — `Badge` itself, the
 * punch map, the movement feed and the profile hero had all already moved to
 * `text-label`. §1 bans 11px for anything a user reads as a word, and a button
 * label is the most word-like thing on a screen: it is the promise of what the
 * click does. `sm` now takes `text-label` (12.5px · 600) with the rest of them,
 * which is also the size the handoff draws its in-card and selection-bar
 * actions at. `--text-badge` keeps its frozen declaration in the token file so
 * nothing stops compiling, but it has no consumers left.
 *
 * TOUCH TARGETS. 32/40/48 is desktop console density and sm is below the 44px
 * touch minimum. Rather than inflate every button on every screen, the base
 * carries a coarse-pointer floor: on a device driven by a finger (the camp
 * tablets, the field phones) every button is at least 44px, while the desktop
 * console keeps its density. `size="touch"` stays as the explicit opt-in for
 * the rare desktop control that also needs the larger target.
 *
 * STATE RAMP. The sister app writes hover/active as literally darker hexes.
 * That cannot be ported: `--primary` is tenant-brandable at runtime and flips
 * to a *light* tone in dark mode, where "darker" is the wrong direction. The
 * ramp is expressed instead as `.state-layer` (see index.css) — a currentColor
 * overlay at 8% hover / 10% focus / 12% press. Because it is tinted with the
 * element's own ink it always moves *away* from the fill: it darkens a light
 * fill and lightens a dark one, for any hue, in either theme. Same three
 * explicit steps, one hue-agnostic mechanism.
 * ======================================================================== */
/* EXPORTED, because a `<Link>` cannot be a `<Button>`.
 *
 * `Button` renders a `<button>` and has no `asChild`, so a control that must
 * be a router link had exactly two options: eight utilities hand-spelling
 * `variant="outline"` at the call site (which is what the device register
 * did), or this. The recipe is the primitive; the element is the call site's.
 * Reach for it ONLY when the element cannot be a `<button>` — a `<Button>` is
 * still the answer everywhere else, and a second `buttonVariants()` call
 * beside a real one is the copy this export exists to remove. */
export const buttonVariants = cva(
  cn(
    'state-layer inline-flex items-center justify-center gap-element',
    'rounded-control font-semibold leading-none',
    // A label never breaks: "Delete draft" on two lines inside one button reads
    // as two controls. A row that runs out of room wraps its BUTTONS instead.
    'whitespace-nowrap',
    // Only paint-level properties transition — never `width`/`height`, which
    // would animate layout on every re-render.
    'transition-[background-color,box-shadow,transform,color] duration-fast ease-standard',
    'active:scale-[0.98]',
    // Focus is a real 2px ring held off the control by 2px, on every variant.
    focusRing,
    // `pointer-events-none` is what keeps `.state-layer` from lighting up a
    // disabled control on hover; the native `disabled` attribute already
    // blocks activation on its own.
    cn('disabled:pointer-events-none disabled:shadow-none', disabledFade),
    '[@media(pointer:coarse)]:min-h-touch'
  ),
  {
    variants: {
      variant: {
        // --- Solid fills -------------------------------------------------
        // `default`/`primary`/`brand` are one look under three names: they all
        // consume `--primary`, which the branding store drives from the tenant
        // brand colour. `brand` is the alias the auth pages use; `primary` is
        // the name the design system uses.
        //
        // ALL SEVEN SOLID FILLS NOW TAKE THE SAME NEUTRAL LIFT, and that is the
        // change. These used to split: the brand three took
        // `--shadow-brand-hover`, a lift tinted with `--primary` that the token
        // file called "the signature move", and the status four took a neutral
        // one on the reasoning that a brand-tinted glow under a red delete
        // button reads as the wrong signal.
        //
        // That reasoning was right and it generalises further than it was
        // applied. The handoff's hover is neutral warm ink for EVERY object
        // (docs/design-rules.md §1) — a shadow says "this is lifted", which is
        // a statement about depth, and depth has no hue. A brand-tinted one
        // also puts the accent somewhere §1 does not list among its three jobs
        // (the active nav marker, the primary action's FILL, and the "on site"
        // series), so the button was spending the brand twice.
        //
        // `--shadow-brand-hover` survives only as a deprecated alias pointing
        // at `--shadow-hover`, so naming it here was already a no-op visually —
        // but it kept the retired idiom alive on the three most-used variants
        // in the app, one alias-deletion away from reappearing in a diff nobody
        // reads. Named roles from here on.
        /* ONE NAME FOR THE BRAND FILL, and it took three to notice.
         *
         * `default`, `primary` and `brand` were the SAME declaration written
         * out three times, so the most important control in the app could be
         * spelled three ways and a reader grepping for one found a third of
         * them. §5 is one name per thing; the two aliases are deleted rather
         * than deprecated, because an alias that still compiles comes back. */
        default: 'bg-primary text-primary-foreground hover:shadow-hover',
        success:
          'bg-success text-success-foreground hover:shadow-hover focus-visible:outline-success',
        warning:
          'bg-warning text-warning-foreground hover:shadow-hover focus-visible:outline-warning',
        info: 'bg-info text-info-foreground hover:shadow-hover focus-visible:outline-info',
        /* Was two names for one look, and this file said so without fixing
           it. `danger` wins: it names the MEANING, where `destructive` named
           what the press does — and the same word is now the `tone` a Dialog
           takes and the variant a Badge takes, so one idea has one spelling
           from `useConfirm` down to the painted button. */
        danger:
          'bg-danger text-danger-foreground hover:shadow-hover focus-visible:outline-danger',

        // --- Low emphasis --------------------------------------------------
        // The sister app's "secondary": a pale fill with a hairline border.
        // `muted` is the recessed tone (it was spelled `surface-container-high`,
        // one of seven names for it), so it stays legible whether it lands on a
        // card or on the page.
        secondary:
          'border border-border bg-muted text-foreground',
        // Deliberately still `bg-transparent`. This is by far the most-used
        // variant in the app (~180 call sites, many of them sitting on cards,
        // toolbars and coloured strips); giving it a fill would change all of
        // them at once with no way to check each. The new shape, type and
        // state layer carry the re-skin here.
        outline: 'border border-border bg-transparent text-foreground',
        ghost: 'text-foreground',

        /* DESTRUCTIVE, AT SECONDARY WEIGHT — the variant three screens had
         * already invented, three different ways, because it did not exist:
         * `variant="outline" className="text-danger"` on the device register,
         * a full hover triple written out on the corrections queue, and a
         * third spelling on the operations control room.
         *
         * It is a real gap, not a stylistic nicety. `danger` is a SOLID fill,
         * which is right for the confirm button inside a dialog — the last
         * click, where the weight IS the warning. It is wrong for a control
         * that merely OPENS that dialog while sitting in a row of ordinary
         * actions: a solid red button in a toolbar reads as the primary thing
         * to do on the screen, which is the opposite of what a destructive
         * secondary means.
         *
         * `border-danger-border`, not `border-danger`: each status family
         * carries a `-border` member measured for a 1.4.11 non-text edge, and
         * the solid fill is far too heavy for a hairline. Hover takes the tint
         * rather than deepening the ink, so the control gains weight without
         * becoming the solid variant it is deliberately not. */
        'danger-outline':
          'border border-danger-border bg-transparent text-danger-on-surface ' +
          'hover:bg-danger-surface focus-visible:outline-danger',
      },
      size: {
        sm: 'min-h-8 px-btn-x-sm py-btn-y-sm text-label',
        md: 'min-h-10 px-btn-x-md py-btn-y-md text-copy',
        lg: 'min-h-12 px-btn-x-lg py-btn-y-lg text-copy-lg',
        // Explicit 44px opt-in (WCAG 2.5.5) for pointer-driven screens.
        touch: 'min-h-touch px-btn-x-md py-btn-y-sm text-copy',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    // NB: `type` is deliberately NOT defaulted to "button" here. It would be
    // the safer HTML default, but some forms in the app rely on the native
    // implicit `submit`, and flipping it would turn those into dead buttons
    // with nothing to catch it. Left as a separate, testable change.
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
);
Button.displayName = 'Button';
